import { t } from '@/plugins/i18n'

// local part: allows quoted strings or dot-separated words
const localPart = '(([^<>()[\\]\\\\.,;:\\s@"]+(\\.[^<>()[\\]\\\\.,;:\\s@"]+)*)|(".+"))'
// @ symbol
const atSymbol = '@'
// Domain part: either IPv4 in brackets or domain name
const domainPart =
  '(' +
  '(\\[\\d{1,3}\\.\\d{1,3}\\.\\d{1,3}\\.\\d{1,3}\\])' + // IPv4 in brackets
  '|' +
  '(([a-zA-Z\\-0-9]+\\.)+[a-zA-Z]{2,})' + // domain name
  ')'
const mailPattern = new RegExp('^' + localPart + atSymbol + domainPart + '$')

const passwordLengthPattern = /.{8,}/
const passwordPattern = /^[\x20-\x7E\xA0-\xFF]{8,}$/
const namePattern = /^[\p{L}0-9\u00C0-\u017F ,.'-]{0,50}$/u
const phonePattern = /^[^a-zA-Z]{0,20}$/
const orderNumberPattern = /^[\p{L}0-9\-_/.#+()[\] ]{3,}$/u
const positiveNumberPattern = /^[+]?\d+([.,]\d+)?$/
const urlPattern = new RegExp(
  '^(https?:\\/\\/)?' + // protocol
    '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
    '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
    '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
    '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
    '(\\#[-a-z\\d_]*)?$', // fragment locator
  'i'
)
export const mailRule = (value: string): boolean | string =>
  !value || value.length === 0 || mailPattern.test(value) || t('error.invalidEmail').toString()

export const nameRule = (value: string): boolean | string =>
  namePattern.test(value) || t('error.invalidName').toString()

export const noInvalidSpacesRule = (value: string): boolean | string => {
  if (!value) return true
  const trimmedValue = value.trim()
  if (value !== trimmedValue || /\s{2,}/.test(value)) return t('error.invalidWhitespace').toString()
  return true
}

export const passwordLengthRule = (value: string): boolean | string =>
  passwordLengthPattern.test(value) || t('error.shortPassword').toString()

export const passwordRule = (value: string): boolean | string =>
  passwordPattern.test(value) || t('error.passwordNotAllowedChar').toString()

export const passwordMatchRule = (password: string) => {
  return (value: string): boolean | string =>
    password === value || t('error.passwordRepeat').toString()
}
export const phoneRule = (value: string | undefined): boolean | string => {
  if (value === undefined) return true
  const spacesCheck = noInvalidSpacesRule(value)
  if (spacesCheck !== true) return spacesCheck
  return phonePattern.test(value) || t('error.invalidNumber').toString()
}

export const requiredRule = (value: string | boolean): boolean | string => {
  if (typeof value === 'string') {
    const trimmedValue = value.trim()
    if (!trimmedValue) return t('error.requiredField').toString()
    const spacesCheck = noInvalidSpacesRule(value)
    if (spacesCheck !== true) return spacesCheck
    return true
  }
  return !!value || t('error.requiredField').toString()
}

export const requiredForDebouncedFieldsRule = (value: string | boolean): boolean | string => {
  if (typeof value === 'string') {
    const trimmedValue = value.trim()
    if (!trimmedValue) return t('error.requiredField').toString()
    return true
  }
  return !!value || t('error.requiredField').toString()
}

export const simpleRequiredRule = (value: string | boolean): boolean | string =>
  !!value || t('error.requiredField').toString()

export const orderNumberRule = (value: string): boolean | string =>
  !value || orderNumberPattern.test(value) || t('error.invalidCharacters').toString()

export const positiveNumberRule = (value: string): boolean | string =>
  positiveNumberPattern.test(value) || t('error.invalidNegativeNumber').toString()

export const postBoxRule = (value: string): boolean | string => {
  if (!value) return true
  const spacesCheck = noInvalidSpacesRule(value)
  if (spacesCheck !== true) return spacesCheck
  return value.length <= 20 || t('maxLength', [20]).toString()
}

export const urlRuleHttpOptional = (value: string): boolean | string => {
  if (!value || value.length === 0) return true
  const spacesCheck = noInvalidSpacesRule(value)
  if (spacesCheck !== true) return spacesCheck
  return urlPattern.test(value) || t('error.invalidUrl').toString()
}

export default {
  orderNumberRule,
  mailRule,
  nameRule,
  noInvalidSpacesRule,
  passwordLengthRule,
  passwordMatchRule,
  passwordRule,
  phoneRule,
  positiveNumberRule,
  postBoxRule,
  requiredRule,
  requiredForDebouncedFieldsRule,
  simpleRequiredRule,
  urlRuleHttpOptional
}
